import {
  KeyboardEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FixedSizeList } from "react-window";
import {
  AssignmentPlan,
  AssignmentSnapshot,
  canMoveEmployee,
  PlannerEmployee,
} from "@/components/appointmentRequest/assignmentPlanner/assignmentPlan";
import { duplicatePlannerEmployeeIds } from "@/components/appointmentRequest/assignmentPlanner/employeeFiltering";
import {
  plannerRowStyle,
  PLANNER_ROW_GAP,
  PLANNER_ROW_HEIGHT,
} from "@/components/appointmentRequest/assignmentPlanner/plannerLayout";
import PlannerEmployeeRow, {
  EmployeeRowData,
} from "@/components/appointmentRequest/assignmentPlanner/plannerEmployeeRow";
import styles from "@/components/appointmentRequest/assignmentPlanner/planner.module.css";

interface Props {
  snapshot: AssignmentSnapshot;
  plan: AssignmentPlan;
  employees: PlannerEmployee[];
  onChoose: (employee: PlannerEmployee) => void;
  withActions?: boolean;
  isDisabled?: (employee: PlannerEmployee) => boolean;
  emptyMessage: string;
  className?: string;
}
const employeeKey = (index: number, data: EmployeeRowData) =>
  data.employees[index].id;

export default function PlannerEmployeeList({
  snapshot,
  plan,
  employees,
  onChoose,
  withActions = false,
  isDisabled,
  emptyMessage,
  className = "",
}: Props) {
  const duplicateIds = useMemo(
    () => duplicatePlannerEmployeeIds(snapshot.employees),
    [snapshot.employees],
  );
  const slots = useMemo(
    () => new Map(snapshot.slots.map((slot) => [slot.id, slot])),
    [snapshot.slots],
  );
  const panel = useRef<HTMLDivElement>(null);
  const viewport = useRef<HTMLDivElement>(null);
  const list = useRef<FixedSizeList<EmployeeRowData>>(null);
  const pendingFocus = useRef<number>();
  const [height, setHeight] = useState(280);
  const virtual = employees.length > 50;
  const data: EmployeeRowData = {
    employees,
    plan,
    duplicateIds,
    slots,
    onChoose,
    withActions,
    isDisabled,
  };
  useLayoutEffect(() => {
    const element = viewport.current;
    if (!element) return;
    const resize = () => {
      if (element.clientHeight > 0) setHeight(element.clientHeight);
    };
    resize();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    return () => observer.disconnect();
  }, [virtual]);
  useLayoutEffect(() => {
    pendingFocus.current = undefined;
    list.current?.scrollTo(0);
  }, [employees]);
  const focusPendingRow = () => {
    if (pendingFocus.current === undefined) return;
    const button = panel.current?.querySelector<HTMLButtonElement>(
      `[data-employee-index="${pendingFocus.current}"] button`,
    );
    if (button) {
      pendingFocus.current = undefined;
      button.focus({ preventScroll: true });
    }
  };
  const navigateRows = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!virtual || !(event.target instanceof HTMLElement)) return;
    const row = event.target.closest<HTMLElement>("[data-employee-index]");
    if (!row && event.target !== panel.current) return;
    if (!row && event.key === "Tab") return;
    const current = row ? Number(row.dataset.employeeIndex) : -1;
    let next: number;
    let direction: number;
    if (event.key === "Home") {
      next = 0;
      direction = 1;
    } else if (event.key === "End") {
      next = employees.length - 1;
      direction = -1;
    } else if (
      event.key === "ArrowDown" ||
      (event.key === "Tab" && !event.shiftKey)
    ) {
      next = current + 1;
      direction = 1;
    } else if (
      event.key === "ArrowUp" ||
      (event.key === "Tab" && event.shiftKey)
    ) {
      next = current - 1;
      direction = -1;
    } else return;
    while (next >= 0 && next < employees.length) {
      const employee = employees[next];
      if (canMoveEmployee(employee) && !isDisabled?.(employee)) {
        event.preventDefault();
        pendingFocus.current = next;
        list.current?.scrollToItem(next, "smart");
        focusPendingRow();
        return;
      }
      next += direction;
    }
  };
  return (
    <div
      ref={panel}
      style={plannerRowStyle}
      className={`${styles.panel} ${virtual ? styles.virtualEmployeePanel : ""} ${className}`}
      role="list"
      tabIndex={virtual ? 0 : undefined}
      onKeyDown={navigateRows}
    >
      {virtual ? (
        <div ref={viewport} className={styles.virtualEmployeeViewport}>
          <FixedSizeList<EmployeeRowData>
            ref={list}
            height={height}
            width="100%"
            itemCount={employees.length}
            itemSize={PLANNER_ROW_HEIGHT + PLANNER_ROW_GAP}
            itemData={data}
            itemKey={employeeKey}
            overscanCount={4}
            onItemsRendered={focusPendingRow}
          >
            {PlannerEmployeeRow}
          </FixedSizeList>
        </div>
      ) : (
        employees.map((employee, index) => (
          <PlannerEmployeeRow
            key={employee.id}
            index={index}
            style={{}}
            data={data}
          />
        ))
      )}
      {!employees.length && <p className={styles.empty}>{emptyMessage}</p>}
    </div>
  );
}
